import {
  externalSolutionKindLabel,
  type ThirdPartySolutionFilterInput,
} from './third-party-solution-edit';
import type { ExternalSolutionKind } from '@shared/types/competitor-service';

export interface CustomerSolutionUsageInput {
  id: string;
  competitor_service_id: string;
  competitor_service_name: string;
}

export interface CompetitorSolutionMasterInput
  extends Omit<ThirdPartySolutionFilterInput, 'name'> {
  id: string;
  name?: string;
}

type UsageClassificationSource = Omit<CompetitorSolutionMasterInput, 'id'>;

export interface ClassifiedSolutionUsage<T extends CustomerSolutionUsageInput> {
  service: T;
  competitorProductNames: string[];
  externalSolutionKind: ExternalSolutionKind;
  externalSolutionKindLabel: string;
}

export interface SolutionUsageClassification<
  T extends CustomerSolutionUsageInput,
> {
  competitorUsages: ClassifiedSolutionUsage<T>[];
  referralUsages: ClassifiedSolutionUsage<T>[];
  otherUsages: ClassifiedSolutionUsage<T>[];
}

/**
 * 1件の外部ソリューションの表示分類。
 *
 * `競合` / `紹介先` / `その他` は外部ソリューション本体の保存値 `kind` を正とする。
 * 顧客の利用行自体には分類を保存しない。
 */
export interface DerivedUsageClassification {
  /** 外部ソリューション本体の保存区分が `competitor` なら true。 */
  isCompetitor: boolean;
  /** 外部ソリューション本体の保存区分が `referral` なら true。 */
  isReferral: boolean;
  /** 保存された外部ソリューション区分。 */
  externalSolutionKind: ExternalSolutionKind;
  /** 顧客詳細で表示する外部ソリューション区分ラベル。 */
  externalSolutionKindLabel: string;
  /** 対象自社ソリューション名 (trim + 重複排除済み)。競合区分の時だけ保持する。 */
  competitorProductNames: string[];
}

export interface SolutionUsageOptionInput {
  name: string;
}

/**
 * 外部ソリューションの保存区分 (`kind`) から表示分類を導く。
 * モーダルの保存前プレビューと一覧分類の双方で同じ判定を使うための単一ロジック。
 */
export function deriveUsageClassification(
  source: readonly string[] | null | undefined | UsageClassificationSource,
): DerivedUsageClassification {
  const input = normalizeUsageClassificationSource(source);
  const names = uniqueNonBlank(input.competitor_product_names ?? []);
  const kind = input.kind ?? 'other';
  const isCompetitor = kind === 'competitor';
  return {
    isCompetitor,
    isReferral: kind === 'referral',
    externalSolutionKind: kind,
    externalSolutionKindLabel: externalSolutionKindLabel(kind),
    competitorProductNames: isCompetitor ? names : [],
  };
}

function normalizeUsageClassificationSource(
  source: readonly string[] | null | undefined | UsageClassificationSource,
): UsageClassificationSource {
  if (source == null) return { kind: 'other', competitor_product_names: [] };
  if (isProductNameList(source)) {
    return {
      kind: 'other',
      competitor_product_names: source,
    };
  }
  return source;
}

function isProductNameList(
  source: readonly string[] | UsageClassificationSource,
): source is readonly string[] {
  return Array.isArray(source);
}

/** 追加 / 編集モーダルの保存可否を判定するための入力状態。 */
export interface SolutionUsageDraftState {
  /** 既存マスターから外部ソリューションを選択済みか (= competitor_service_id 確定)。 */
  hasSelection: boolean;
  /** 'YYYY-MM-DD' か空文字列。 */
  renewalRejectionDeadline: string;
  /** 'YYYY-MM-DD' か空文字列。 */
  contractEndDate: string;
}

/**
 * 利用ソリューションの保存前バリデーション (純粋関数)。 エラーメッセージを返すか、
 * 問題なければ `null` を返す。 モーダルと単体テストで同じ判定を共有する。
 *
 * - 候補未選択 (competitor_service_id 未確定) では保存不可。検索文字列だけでは
 *   保存できない。
 * - 日付は区分によらず任意。 競合でも未入力のまま保存できる。
 * - 両日付があるとき、契約終了日は更新拒絶期限日以降でなければならない。
 */
export function validateSolutionUsageDraft(
  state: SolutionUsageDraftState,
): string | null {
  if (!state.hasSelection) {
    return '登録済みの外部ソリューションを選択してください。';
  }
  if (
    state.renewalRejectionDeadline &&
    state.contractEndDate &&
    state.contractEndDate < state.renewalRejectionDeadline
  ) {
    return '契約終了日は更新拒絶期限日以降の日付にしてください。';
  }
  return null;
}

/**
 * 利用ソリューション追加モーダルの候補表示用フィルタ。
 *
 * - 未入力時は「選択肢から選ぶ」動線として全候補を返す。
 * - 入力中は部分一致で候補を絞る。
 */
export function filterSolutionUsageOptions<T extends SolutionUsageOptionInput>(
  options: T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;
  return options.filter((option) => option.name.toLowerCase().includes(needle));
}

export function classifySolutionUsages<T extends CustomerSolutionUsageInput>(
  usages: T[],
  masters: CompetitorSolutionMasterInput[],
): SolutionUsageClassification<T> {
  const classificationByServiceId = new Map(
    masters.map((master) => [master.id, deriveUsageClassification(master)]),
  );
  const competitorUsages: ClassifiedSolutionUsage<T>[] = [];
  const referralUsages: ClassifiedSolutionUsage<T>[] = [];
  const otherUsages: ClassifiedSolutionUsage<T>[] = [];

  for (const service of usages) {
    const derived =
      classificationByServiceId.get(service.competitor_service_id) ??
      deriveUsageClassification(null);
    const row = {
      service,
      competitorProductNames: derived.competitorProductNames,
      externalSolutionKind: derived.externalSolutionKind,
      externalSolutionKindLabel: derived.externalSolutionKindLabel,
    };
    if (derived.isCompetitor) {
      competitorUsages.push(row);
    } else if (derived.isReferral) {
      referralUsages.push(row);
    } else {
      otherUsages.push(row);
    }
  }

  return { competitorUsages, referralUsages, otherUsages };
}

function uniqueNonBlank(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }

  return result;
}
